import { flatten, uniqueId } from "lodash";
import { psoResultsService as service } from "services/serviceStorage";
import { BaseApiResponse } from "common/types";
import { wrapRequest } from "common/utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    MasterFilterState,
    DetailFilterState,
    Scenario,
    FileGroups,
    MappedFileGroups
} from "./types";

// request static reports source
export const fetchScenariosSource = createAsyncThunk<{ data: Scenario[]; total: number; filter: MasterFilterState }, MasterFilterState>(
    'psoResults/fetchScenariosSource',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: Scenario[]; totalCount: number; }>>({
            handler: () => service.getScenarios(filter),
            showNotifications: true
        });

        const mappedData = {
            data: data?.result.items.map(i => ({ ...i, idx: i.scenarioId })) || [],
            total: data?.result.totalCount || 0,
            filter: filter
        };

        return mappedData;
    }
);

// request cases source
export const fetchFilesSource = createAsyncThunk<{ data: MappedFileGroups[]; total: number; filter: DetailFilterState }, DetailFilterState>(
    'psoResults/fetchFilesSource',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<FileGroups[]>>({
            handler: () => service.getResultFiles(filter),
            showNotifications: true
        });

        const mappedData = {
            data: data?.result.map(i => ({
                ...i,
                idx: uniqueId(String(i.groupSize)),
                files: i.files.map(f => ({
                    ...f,
                    idx: uniqueId(f.fileName),
                })),
            })) || [],
            total: flatten(data?.result.map(r => r.files)).length || 0,
            filter: filter,
        };

        return mappedData;
    }
);

export const getBasicInfo = createAsyncThunk<{ hasTemplate: boolean }, void>(
    'psoResults/getBasicInfo',
    async () => {
        const { data } = await wrapRequest<BaseApiResponse<{ hasTemplate: boolean }>>({
            handler: () => service.getBaseInfo(),
            showNotifications: true
        });

        return data?.result!;
    }
);

export const reMerge = createAsyncThunk<void, number>(
    'psoResults/reMerge',
    async (scenarioId, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => service.reMerge({ scenarioId }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const getDownloadUrls = createAsyncThunk<string[], { fileNames: string[]; scenarioId: number; }>(
    'psoResults/getDownloadUrls',
    async (request) => {
        const { data } = await wrapRequest<BaseApiResponse<string[]>>({
            handler: () => service.getDownloadUrls(request),
            showNotifications: true
        });

        return data?.result!;
    }
);

export const getTemplate = createAsyncThunk<string[], void>(
    'psoResults/getTemplate',
    async () => {
        const { data } = await wrapRequest<BaseApiResponse<string[]>>({
            handler: () => service.getTemplate(),
            showNotifications: true
        });

        return data?.result!;
    }
);

export const saveTemplate = createAsyncThunk<void, string[]>(
    'psoResults/saveTemplate',
    async (files, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => service.saveTemplate({ files }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const stopJobs = createAsyncThunk<void, number>(
    'psoResults/stopJobs',
    async (scenarioId, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => service.stopJobs(scenarioId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);